import { Box, Typography, Stack, Button, ButtonGroup, Chip, CircularProgress, Sheet, Textarea, Switch, IconButton, Input, Alert, LinearProgress, Tab, TabList, TabPanel, Tabs, Checkbox, Modal, ModalDialog, ModalClose, DialogContent, FormControl, FormLabel, Select, Option } from "@mui/joy";
import { useContext, useEffect, useState, useRef, useCallback } from "react";
import { LayoutContext } from "../context";
import { APIGetModuleDetail, APIMarkModuleRead, APIPracticeModule, APIUpdateModule, APIGenerateModuleContent, APIUploadPDF, APIExpandModuleContent, APIGetAvailableCategories, APIMoveModule, APIGetPlanDetail, APIUploadQuestionImage, APIGetModuleRecentTests, detectTrialLocked } from "../api";
import { useParams, useNavigate, useSearchParams, Link as RouterLink } from "react-router";
import { SafeHTML } from "../utils";
import { ArrowLeft, ArrowRight, Exam, CheckCircle, PencilSimple, FloppyDisk, X, Sparkle, UploadSimple, TextT, ArrowsOut, ArrowsIn, Brain, Lightning, Translate, BookOpen, FolderOpen, Lock, TextAa, TextB, TextItalic, TextUnderline, ListBullets, ListNumbers, Table as TableIcon, ArrowCounterClockwise, ArrowClockwise, Code, ImageSquare } from "@phosphor-icons/react";

import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Image from '@tiptap/extension-image';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';


// ========================================
// WYSIWYG Editor (Tiptap)
// ========================================
function RichTextEditor({ content, onChange, token }) {
    const [showSource, setShowSource] = useState(false);
    const [sourceCode, setSourceCode] = useState('');

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [3, 4] },
            }),
            Underline,
            Image,
            Table.configure({ resizable: false }),
            TableRow,
            TableCell,
            TableHeader,
        ],
        content: content || '',
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());
        },
    });

    // Sync content when it changes externally
    useEffect(() => {
        if (editor && content !== editor.getHTML()) {
            editor.commands.setContent(content || '');
        }
    }, [content]);

    const toggleSource = useCallback(() => {
        if (!showSource) {
            setSourceCode(editor?.getHTML() || '');
        } else {
            // Apply source code changes
            if (editor) {
                editor.commands.setContent(sourceCode);
                onChange(sourceCode);
            }
        }
        setShowSource(!showSource);
    }, [showSource, editor, sourceCode, onChange]);

    const addImage = useCallback(() => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async (event) => {
            const file = event.target.files[0];
            if (!file || !token) return;
            try {
                const res = await APIUploadQuestionImage(token, file);
                if (res.success && editor) {
                    editor.chain().focus().setImage({ src: res.url }).run();
                }
            } catch (e) { console.error('Image upload error:', e); }
        };
        input.click();
    }, [editor, token]);

    if (!editor) return null;

    const ToolBtn = ({ onClick, active, children, title }) => (
        <IconButton
            size="sm"
            variant={active ? 'soft' : 'plain'}
            color={active ? 'primary' : 'neutral'}
            onClick={onClick}
            title={title}
            sx={{
                minWidth: 32, minHeight: 32,
                borderRadius: 'sm',
                ...(active && { bgcolor: 'rgba(218,119,86,0.15)', color: '#B84C31' }),
            }}
        >
            {children}
        </IconButton>
    );

    return (
        <Box sx={{ border: '1px solid', borderColor: 'neutral.300', borderRadius: 'lg', overflow: 'hidden' }}>
            {/* Toolbar */}
            <Stack
                direction="row"
                spacing={0.25}
                sx={{
                    p: 0.75,
                    borderBottom: '1px solid',
                    borderColor: 'neutral.200',
                    bgcolor: 'background.level1',
                    flexWrap: 'wrap',
                    gap: 0.25,
                }}
            >
                <ToolBtn onClick={() => editor.chain().focus().undo().run()} title="Отменить">
                    <ArrowCounterClockwise size={18} />
                </ToolBtn>
                <ToolBtn onClick={() => editor.chain().focus().redo().run()} title="Повторить">
                    <ArrowClockwise size={18} />
                </ToolBtn>

                <Box sx={{ width: '1px', bgcolor: 'neutral.300', mx: 0.5, alignSelf: 'stretch' }} />

                <ToolBtn
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                    active={editor.isActive('heading', { level: 3 })}
                    title="Заголовок H3"
                >
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, lineHeight: 1 }}>H3</Typography>
                </ToolBtn>
                <ToolBtn
                    onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
                    active={editor.isActive('heading', { level: 4 })}
                    title="Подзаголовок H4"
                >
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, lineHeight: 1 }}>H4</Typography>
                </ToolBtn>

                <Box sx={{ width: '1px', bgcolor: 'neutral.300', mx: 0.5, alignSelf: 'stretch' }} />

                <ToolBtn
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    active={editor.isActive('bold')}
                    title="Жирный"
                >
                    <TextB size={18} />
                </ToolBtn>
                <ToolBtn
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    active={editor.isActive('italic')}
                    title="Курсив"
                >
                    <TextItalic size={18} />
                </ToolBtn>
                <ToolBtn
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    active={editor.isActive('underline')}
                    title="Подчёркивание"
                >
                    <TextUnderline size={18} />
                </ToolBtn>

                <Box sx={{ width: '1px', bgcolor: 'neutral.300', mx: 0.5, alignSelf: 'stretch' }} />

                <ToolBtn
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    active={editor.isActive('bulletList')}
                    title="Маркированный список"
                >
                    <ListBullets size={18} />
                </ToolBtn>
                <ToolBtn
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    active={editor.isActive('orderedList')}
                    title="Нумерованный список"
                >
                    <ListNumbers size={18} />
                </ToolBtn>

                <Box sx={{ width: '1px', bgcolor: 'neutral.300', mx: 0.5, alignSelf: 'stretch' }} />

                <ToolBtn
                    onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
                    title="Вставить таблицу"
                >
                    <TableIcon size={18} />
                </ToolBtn>
                <ToolBtn onClick={addImage} title="Вставить изображение">
                    <ImageSquare size={18} />
                </ToolBtn>

                <Box sx={{ flex: 1 }} />

                <ToolBtn
                    onClick={toggleSource}
                    active={showSource}
                    title={showSource ? 'Визуальный редактор' : 'Исходный код'}
                >
                    <Code size={18} />
                </ToolBtn>
            </Stack>

            {/* Editor / Source */}
            {showSource ? (
                <Textarea
                    value={sourceCode}
                    onChange={(e) => setSourceCode(e.target.value)}
                    minRows={20}
                    maxRows={60}
                    sx={{
                        border: 'none',
                        borderRadius: 0,
                        fontFamily: 'monospace',
                        fontSize: 'sm',
                        '--Textarea-focusedThickness': '0px',
                    }}
                />
            ) : (
                <Box
                    sx={{
                        p: 2,
                        minHeight: 400,
                        '& .tiptap': {
                            outline: 'none',
                            minHeight: 380,
                            '& h3': { mt: 2.5, mb: 1, fontSize: '1.25rem', fontWeight: 700 },
                            '& h4': { mt: 2, mb: 0.75, fontSize: '1.1rem', fontWeight: 600 },
                            '& p': { mb: 1, lineHeight: 1.7 },
                            '& ul, & ol': { mb: 1, pl: 3 },
                            '& li': { mb: 0.3 },
                            '& strong': { fontWeight: 700 },
                            '& table': {
                                width: '100%', borderCollapse: 'collapse', mb: 2,
                                '& th, & td': {
                                    border: '1px solid',
                                    borderColor: 'neutral.300',
                                    p: 1,
                                    minWidth: 60,
                                },
                                '& th': { fontWeight: 700, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(237, 232, 223, 0.10)' : '#EDE8DF', textAlign: 'left', color: (theme) => theme.palette.mode === 'dark' ? 'inherit' : 'inherit' },
                            },
                            '& img': { maxWidth: '100%', height: 'auto', borderRadius: '4px' },
                            '& p.is-editor-empty:first-child::before': {
                                content: '"Начните вводить текст..."',
                                color: 'var(--joy-palette-text-tertiary)',
                                float: 'left',
                                height: 0,
                                pointerEvents: 'none',
                            },
                        },
                    }}
                >
                    <EditorContent editor={editor} />
                </Box>
            )}
        </Box>
    );
}


// ========================================
// Move Module Modal
// ========================================
function MoveModuleModal({ open, onClose, token, moduleId, currentCategoryPath, onMoved }) {
    const [tree, setTree] = useState([]);
    const [loading, setLoading] = useState(false);
    const [moving, setMoving] = useState(false);
    const [error, setError] = useState('');

    const [selectedDiscipline, setSelectedDiscipline] = useState(null);
    const [selectedSubsection, setSelectedSubsection] = useState(null);
    const [selectedTopic, setSelectedTopic] = useState(null);

    useEffect(() => {
        if (open) {
            setSelectedDiscipline(null);
            setSelectedSubsection(null);
            setSelectedTopic(null);
            setError('');
            loadCategories();
        }
    }, [open]);

    const loadCategories = async () => {
        setLoading(true);
        try {
            const res = await APIGetAvailableCategories(token, moduleId);
            if (res.success) setTree(res.tree || []);
        } catch (e) {
            setError('Ошибка загрузки категорий');
        } finally {
            setLoading(false);
        }
    };

    const handleMove = async () => {
        if (!selectedTopic) return;
        setMoving(true);
        setError('');
        try {
            const res = await APIMoveModule(token, moduleId, selectedTopic);
            if (res.success) {
                onMoved(res.module);
            } else {
                setError(res.error || 'Ошибка перемещения');
            }
        } catch (e) {
            setError(e.response?.data?.error || 'Ошибка перемещения');
        } finally {
            setMoving(false);
        }
    };

    const subsections = tree.find(d => d.id === selectedDiscipline)?.subsections || [];
    const topics = subsections.find(s => s.id === selectedSubsection)?.topics || [];

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={{ width: { xs: '95%', sm: 480 }, borderRadius: 'xl' }}>
                <ModalClose />
                <DialogContent>
                    <Stack spacing={2.5}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <FolderOpen color="#B84C31" />
                            <Typography level="title-lg">Переместить модуль</Typography>
                        </Stack>

                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                            Текущая категория: <strong>{currentCategoryPath}</strong>
                        </Typography>

                        {error && <Alert color="danger" variant="soft" size="sm">{error}</Alert>}

                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress size="sm" />
                            </Box>
                        ) : (
                            <>
                                <FormControl>
                                    <FormLabel>Дисциплина</FormLabel>
                                    <Select
                                        placeholder="Выберите дисциплину"
                                        value={selectedDiscipline}
                                        onChange={(_, v) => {
                                            setSelectedDiscipline(v);
                                            setSelectedSubsection(null);
                                            setSelectedTopic(null);
                                        }}
                                    >
                                        {tree.map(d => (
                                            <Option key={d.id} value={d.id}>{d.name}</Option>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl disabled={!selectedDiscipline}>
                                    <FormLabel>Раздел</FormLabel>
                                    <Select
                                        placeholder="Выберите раздел"
                                        value={selectedSubsection}
                                        onChange={(_, v) => {
                                            setSelectedSubsection(v);
                                            setSelectedTopic(null);
                                        }}
                                    >
                                        {subsections.map(s => (
                                            <Option key={s.id} value={s.id}>
                                                {s.name}
                                                <Chip size="sm" variant="soft" sx={{ ml: 1 }}>
                                                    {s.topics.length}
                                                </Chip>
                                            </Option>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl disabled={!selectedSubsection}>
                                    <FormLabel>Тема (целевая категория)</FormLabel>
                                    <Select
                                        placeholder="Выберите тему"
                                        value={selectedTopic}
                                        onChange={(_, v) => setSelectedTopic(v)}
                                    >
                                        {topics.map(t => (
                                            <Option key={t.id} value={t.id}>{t.name}</Option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </>
                        )}

                        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                            <Button variant="plain" color="neutral" onClick={onClose}>
                                Отмена
                            </Button>
                            <Button
                                variant="solid"
                                disabled={!selectedTopic}
                                loading={moving}
                                startDecorator={<FolderOpen size={16} />}
                                onClick={handleMove}
                            >
                                Переместить
                            </Button>
                        </Stack>
                    </Stack>
                </DialogContent>
            </ModalDialog>
        </Modal>
    );
}


export default function ModuleDetailPage() {
    const { token, user, sidebarHidden, setSidebarHidden, openUpgradeModal } = useContext(LayoutContext);
    const { moduleId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const planId = searchParams.get('planId');
    const fromPlan = searchParams.get('fromPlan');
    const roadmapId = searchParams.get('roadmapId');
    const rmId = searchParams.get('rmId');

    const [wideText, setWideText] = useState(() => localStorage.getItem('module_wide_text') === 'true');
    const toggleWideText = () => setWideText(prev => { const v = !prev; localStorage.setItem('module_wide_text', v); return v; });

    const [module, setModule] = useState(null);
    const [loading, setLoading] = useState(true);
    const [practicing, setPracticing] = useState(false);
    const [recentTests, setRecentTests] = useState([]);
    const [marking, setMarking] = useState(false);
    const [moduleLimit, setModuleLimit] = useState(false);
    const [prevModule, setPrevModule] = useState(null);
    const [nextModule, setNextModule] = useState(null);

    // Admin edit state
    const [editing, setEditing] = useState(false);
    const [editContent, setEditContent] = useState('');
    const [editTitle, setEditTitle] = useState('');
    const [editPublished, setEditPublished] = useState(false);
    const [saving, setSaving] = useState(false);

    // AI generation state
    const [generating, setGenerating] = useState(false);
    const [genError, setGenError] = useState('');
    const [genPreview, setGenPreview] = useState('');
    const [sourceText, setSourceText] = useState('');
    const [pdfPath, setPdfPath] = useState('');
    const [pdfInfo, setPdfInfo] = useState(null);
    const [startPage, setStartPage] = useState(0);
    const [pageCount, setPageCount] = useState(5);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    // Expand state
    const [expanding, setExpanding] = useState(false);
    const [expandError, setExpandError] = useState('');
    const [expandPreview, setExpandPreview] = useState('');

    // View mode
    const [viewMode, setViewMode] = useState('high_yield');

    // Language toggle (EN/RU)
    const [lang, setLang] = useState(() => {
        try { return localStorage.getItem('moduleLang') || 'en'; }
        catch { return 'en'; }
    });

    // Move module
    const [moveModalOpen, setMoveModalOpen] = useState(false);

    // Font size
    const [fontSize, setFontSize] = useState(() => {
        try { return parseInt(localStorage.getItem('moduleFontSize')) || 16; }
        catch { return 16; }
    });

    const changeFontSize = (delta) => {
        setFontSize(prev => {
            const next = Math.min(24, Math.max(12, prev + delta));
            localStorage.setItem('moduleFontSize', next);
            return next;
        });
    };

    const isAdmin = user?.role === 'admin';
    const hasContent = module && module.content && module.content.trim().length > 0;
    const hasExpanded = module?.has_expanded;
    const hasRussian = module?.has_russian;

    // Post-onboarding tour: show a slim banner if the user is mid-tour.
    // The tour is considered active when LS_KEYS.pending === '1' and LS_KEYS.done !== '1'.
    // We read once on mount; no need to re-check (the user will navigate back to /plans
    // to advance the tour anyway).
    const [tourBannerVisible] = useState(() => {
        try {
            return localStorage.getItem('apex_tour_pending') === '1'
                && localStorage.getItem('apex_tour_done') !== '1';
        } catch { return false; }
    });

    const returnToPlan = () => {
        setSidebarHidden(false);
        if (fromPlan === 'v2' && roadmapId && rmId) {
            navigate(`/plans?roadmapId=${roadmapId}&rmId=${rmId}`);
        } else {
            navigate('/plans');
        }
    };

    const toggleLang = () => {
        const next = lang === 'en' ? 'ru' : 'en';
        setLang(next);
        try { localStorage.setItem('moduleLang', next); } catch {}
    };

    useEffect(() => {
        loadModule();
    }, [moduleId]);

    // Если пришли из учебного плана — подменяем prev/next по шагам плана
    useEffect(() => {
        if (!planId || !module) return;
        const loadPlanNav = async () => {
            try {
                const res = await APIGetPlanDetail(token, planId);
                if (!res.success) return;
                // Собираем плоский список module_id из всех шагов плана
                const steps = [];
                for (const week of res.plan.weeks || []) {
                    for (const step of week.steps || []) {
                        if (step.module_id) {
                            steps.push({ module_id: step.module_id, title: step.title });
                        }
                    }
                }
                const currentIdx = steps.findIndex(s => s.module_id === parseInt(moduleId));
                if (currentIdx === -1) return;
                setPrevModule(currentIdx > 0 ? { id: steps[currentIdx - 1].module_id, title: steps[currentIdx - 1].title } : null);
                setNextModule(currentIdx < steps.length - 1 ? { id: steps[currentIdx + 1].module_id, title: steps[currentIdx + 1].title } : null);
            } catch (e) {
                console.error('Error loading plan nav:', e);
            }
        };
        loadPlanNav();
    }, [planId, module, moduleId]);

    const loadModule = async () => {
        setLoading(true);
        setModuleLimit(false);
        try {
            const response = await APIGetModuleDetail(token, moduleId);
            if (response.success) {
                setModule(response.module);
                setEditContent(response.module.content);
                setEditTitle(response.module.title);
                setEditPublished(response.module.is_published);
                setPrevModule(response.prev_module || null);
                setNextModule(response.next_module || null);
            }
            // Load user's recent tests for this module (best-effort, non-blocking)
            try {
                const tRes = await APIGetModuleRecentTests(token, moduleId);
                if (tRes.success) setRecentTests(tRes.tests || []);
            } catch (_) { /* ignore */ }
        } catch (error) {
            // Trial lock — модуль не входит в триальный пул. Открываем UpgradeModal
            // с reason из бэка и уводим юзера назад (чтобы не сидел на пустом экране).
            const trialLock = detectTrialLocked(error);
            if (trialLock) {
                openUpgradeModal(trialLock.message);
                navigate(-1);
                return;
            }
            if (error.response?.status === 403 && error.response?.data?.error === 'module_limit') {
                setModuleLimit(true);
            } else {
                console.error('Error loading module:', error);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleToggleRead = async () => {
        setMarking(true);
        try {
            const response = await APIMarkModuleRead(token, moduleId);
            if (response.success) {
                setModule(prev => prev ? { ...prev, is_read: response.is_read } : prev);
            }
        } catch (error) {
            console.error('Error toggling read:', error);
        } finally {
            setMarking(false);
        }
    };

    const handlePractice = async () => {
        setPracticing(true);
        try {
            const response = await APIPracticeModule(token, moduleId);
            if (response.success) {
                navigate(`/test-session/${response.test.id}`);
            }
        } catch (error) {
            const trialLock = detectTrialLocked(error);
            if (trialLock) {
                openUpgradeModal(trialLock.message);
                return;
            }
            console.error('Error starting practice:', error);
            alert('Ошибка: ' + (error.response?.data?.error || 'Не удалось создать тест'));
        } finally {
            setPracticing(false);
        }
    };

    const handleSaveEdit = async () => {
        setSaving(true);
        try {
            const response = await APIUpdateModule(token, moduleId, {
                title: editTitle,
                content: editContent,
                is_published: editPublished
            });
            if (response.success) {
                setModule(response.module);
                setEditing(false);
            }
        } catch (error) {
            console.error('Error saving module:', error);
        } finally {
            setSaving(false);
        }
    };

    // ===== AI Generation =====

    const handleUploadPDF = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        setGenError('');
        try {
            const response = await APIUploadPDF(token, file);
            if (response.success) {
                setPdfPath(response.pdf_path);
                setPdfInfo({
                    filename: response.filename,
                    totalPages: response.total_pages
                });
                setStartPage(0);
                setPageCount(Math.min(5, response.total_pages));
            }
        } catch (error) {
            setGenError('Ошибка загрузки PDF: ' + (error.response?.data?.error || error.message));
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleGenerate = async (mode) => {
        setGenerating(true);
        setGenError('');
        setGenPreview('');

        const data = { category_id: module.category_id };

        if (mode === 'pdf') {
            if (!pdfPath) {
                setGenError('Сначала загрузите PDF файл');
                setGenerating(false);
                return;
            }
            data.pdf_path = pdfPath;
            data.start_page = startPage;
            data.page_count = pageCount;
        } else {
            if (!sourceText.trim()) {
                setGenError('Введите исходный текст');
                setGenerating(false);
                return;
            }
            data.source_text = sourceText;
        }

        try {
            const response = await APIGenerateModuleContent(token, data);
            if (response.success) {
                setGenPreview(response.content);
                setEditContent(response.content);
            } else {
                setGenError(response.error || 'Ошибка генерации');
            }
        } catch (error) {
            setGenError('Ошибка: ' + (error.response?.data?.error || error.message));
        } finally {
            setGenerating(false);
        }
    };

    const handleSaveGenerated = async (publish = false) => {
        setSaving(true);
        try {
            const response = await APIUpdateModule(token, moduleId, {
                content: genPreview || editContent,
                is_published: publish,
                generated_from: 'ai',
                source_text: sourceText
            });
            if (response.success) {
                setModule(response.module);
                setGenPreview('');
                setSourceText('');
                setEditing(false);
            }
        } catch (error) {
            console.error('Error saving generated content:', error);
        } finally {
            setSaving(false);
        }
    };

    // ===== Expand content =====

    const handleExpandContent = async () => {
        setExpanding(true);
        setExpandError('');
        try {
            const response = await APIExpandModuleContent(token, moduleId);
            if (response.success) {
                setExpandPreview(response.content);
            } else {
                setExpandError(response.error || 'Ошибка расширения');
            }
        } catch (error) {
            setExpandError('Ошибка: ' + (error.response?.data?.error || error.message));
        } finally {
            setExpanding(false);
        }
    };

    const handleSaveExpanded = async () => {
        setSaving(true);
        try {
            const response = await APIUpdateModule(token, moduleId, {
                content_expanded: expandPreview,
            });
            if (response.success) {
                setModule(response.module);
                setExpandPreview('');
                setViewMode('explanations');
            }
        } catch (error) {
            console.error('Error saving expanded content:', error);
        } finally {
            setSaving(false);
        }
    };

    // ===== Render =====

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (moduleLimit) {
        return (
            <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 600, mx: 'auto' }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                    <IconButton component={RouterLink} to="/modules" variant="plain">
                        <ArrowLeft />
                    </IconButton>
                    <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                        Назад к модулям
                    </Typography>
                </Stack>
                <Sheet variant="outlined" sx={{ p: 4, borderRadius: 'xl', textAlign: 'center' }}>
                    <Typography level="h3" sx={{ mb: 1.5 }}>
                        Дневной лимит достигнут
                    </Typography>
                    <Typography level="body-md" sx={{ color: 'text.secondary', mb: 2 }}>
                        Вы просмотрели 10 модулей сегодня. Получите полный доступ для безлимитного чтения.
                    </Typography>
                    <Button
                        component={RouterLink}
                        to="/modules"
                        variant="solid"
                    >
                        Вернуться к модулям
                    </Button>
                </Sheet>
            </Box>
        );
    }

    if (!module) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography level="h3">Модуль не найден</Typography>
                <Button variant="plain" onClick={() => {
                    if (fromPlan === 'v2' && roadmapId && rmId) {
                        navigate(`/plans?roadmapId=${roadmapId}&rmId=${rmId}`);
                    } else {
                        navigate('/modules');
                    }
                }} sx={{ mt: 2 }}>
                    {fromPlan === 'v2' ? 'Вернуться к плану' : 'Вернуться к модулям'}
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: wideText ? (sidebarHidden ? 1800 : 1400) : (sidebarHidden ? 1100 : 900), mx: 'auto', transition: 'max-width 0.3s ease' }}>
            {/* Guided tour banner — sticky, impossible to miss */}
            {tourBannerVisible && (
                <Box
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1100,
                        mx: { xs: -2, sm: -3 },
                        mb: 2.5,
                    }}
                >
                    <Sheet
                        variant="soft"
                        sx={{
                            px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 1.75 },
                            bgcolor: '#DA7756',
                            display: 'flex', alignItems: 'center', gap: 1.5,
                            flexWrap: 'wrap',
                            boxShadow: '0 4px 20px rgba(184,76,49,0.35)',
                        }}
                    >
                        <BookOpen size={24} color="#FFFFFF" weight="duotone" />
                        <Box sx={{ flex: 1, minWidth: 140 }}>
                            <Typography level="title-sm" sx={{ color: '#FFFFFF', fontWeight: 700 }}>
                                Ты в туре — прочти и вернись
                            </Typography>
                            <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                Прочитай теорию. Потом нажми кнопку — тур продолжится.
                            </Typography>
                        </Box>
                        <Button
                            size="md"
                            variant="solid"
                            onClick={returnToPlan}
                            startDecorator={<ArrowLeft size={16} />}
                            sx={{
                                bgcolor: '#FFFFFF',
                                color: '#B84C31',
                                fontWeight: 700,
                                px: 3,
                                '&:hover': { bgcolor: '#FFF8F0' },
                            }}
                        >
                            Назад к плану
                        </Button>
                    </Sheet>
                </Box>
            )}

            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                <IconButton variant="plain" onClick={() => {
                    setSidebarHidden(false);
                    if (fromPlan === 'v2' && roadmapId && rmId) {
                        navigate(`/plans?roadmapId=${roadmapId}&rmId=${rmId}`);
                    } else {
                        navigate('/modules');
                    }
                }}>
                    <ArrowLeft />
                </IconButton>
                <Typography level="body-sm" sx={{ color: 'text.tertiary', flex: 1 }}>
                    {module.category_path}
                </Typography>
                <Button
                    variant="plain"
                    size="sm"
                    onClick={toggleWideText}
                    title={wideText ? 'Узкий текст' : 'Широкий текст'}
                    sx={{ color: 'text.tertiary', fontSize: '0.7rem', fontWeight: 500, minWidth: 0, px: 1, '&:hover': { color: 'text.primary' } }}
                >
                    {wideText ? 'Narrow' : 'Wide'}
                </Button>
                <IconButton
                    variant="plain"
                    size="sm"
                    onClick={() => setSidebarHidden(!sidebarHidden)}
                    title={sidebarHidden ? 'Показать боковую панель' : 'На весь экран'}
                    sx={{ color: 'text.tertiary' }}
                >
                    {sidebarHidden ? <ArrowsIn /> : <ArrowsOut />}
                </IconButton>
            </Stack>

            {/* Title + admin controls */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                    {editing ? (
                        <Textarea
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            sx={{ mb: 1, fontSize: '1.5rem', fontWeight: 700 }}
                        />
                    ) : (
                        <Typography level="h1">{lang === 'ru' && hasRussian && module.title_ru ? module.title_ru : module.title}</Typography>
                    )}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                        <Chip size="sm" variant="soft" startDecorator={<Exam size={14} />}>
                            {module.question_count} вопросов
                        </Chip>
                        {hasContent && !editing && !genPreview && (
                            <Checkbox
                                size="sm"
                                label={module.is_read ? 'Прочитано' : 'Отметить как прочитанное'}
                                checked={module.is_read}
                                onChange={handleToggleRead}
                                disabled={marking}
                                sx={{
                                    ml: 1,
                                    fontSize: 'xs',
                                    fontWeight: module.is_read ? 600 : 400,
                                    color: module.is_read ? '#B84C31' : 'text.tertiary',
                                    '& .MuiCheckbox-checkbox': {
                                        borderRadius: '50%',
                                        width: 18,
                                        height: 18,
                                        ...(module.is_read && {
                                            bgcolor: '#DA7756',
                                            borderColor: '#DA7756',
                                            color: '#fff',
                                            '&:hover': { bgcolor: '#B84C31' },
                                        }),
                                    },
                                }}
                            />
                        )}
                    </Stack>
                </Box>
                {isAdmin && !editing && hasContent && (
                    <IconButton variant="outlined" onClick={() => setEditing(true)}>
                        <PencilSimple />
                    </IconButton>
                )}
            </Stack>

            {/* Admin edit controls */}
            {editing && (
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Switch
                        checked={editPublished}
                        onChange={(e) => setEditPublished(e.target.checked)}
                    />
                    <Typography level="body-sm">Опубликован</Typography>
                    <Box sx={{ flex: 1 }} />
                    <Button
                        variant="plain"
                        color="neutral"
                        startDecorator={<X />}
                        onClick={() => {
                            setEditing(false);
                            setEditContent(module.content);
                            setEditTitle(module.title);
                            setEditPublished(module.is_published);
                        }}
                    >
                        Отмена
                    </Button>
                    <Button
                        startDecorator={<FloppyDisk />}
                        onClick={handleSaveEdit}
                        loading={saving}
                    >
                        Сохранить
                    </Button>
                </Stack>
            )}

            {/* ===================== */}
            {/* ADMIN: Empty module — AI generation panel */}
            {/* ===================== */}
            {isAdmin && !hasContent && !genPreview && (
                <Sheet variant="outlined" sx={{ p: 3, borderRadius: 'xl', mb: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                        <Sparkle color="#D97706" />
                        <Typography level="title-md" fontWeight={600}>
                            Генерация содержания
                        </Typography>
                    </Stack>
                    <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2.5 }}>
                        Загрузите PDF страницы из учебника или вставьте текст — AI создаст учебный материал.
                    </Typography>

                    {genError && (
                        <Alert color="danger" variant="soft" sx={{ mb: 2 }}>
                            {genError}
                        </Alert>
                    )}

                    <Tabs defaultValue="pdf" sx={{ bgcolor: 'transparent' }}>
                        <TabList disableUnderline sx={{ gap: 1, mb: 2 }}>
                            <Tab value="pdf" variant="plain" sx={{ fontSize: 'sm', fontWeight: 500, borderRadius: 'md' }}>
                                <UploadSimple size={16} style={{ marginRight: 4 }} />
                                PDF
                            </Tab>
                            <Tab value="text" variant="plain" sx={{ fontSize: 'sm', fontWeight: 500, borderRadius: 'md' }}>
                                <TextT size={16} style={{ marginRight: 4 }} />
                                Текст
                            </Tab>
                        </TabList>

                        {/* === PDF Tab === */}
                        <TabPanel value="pdf" sx={{ p: 0 }}>
                            <Stack spacing={2}>
                                {/* Upload */}
                                <Box>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".pdf"
                                        style={{ display: 'none' }}
                                        onChange={handleUploadPDF}
                                    />
                                    <Button
                                        variant="outlined"
                                        color="neutral"
                                        startDecorator={<UploadSimple />}
                                        onClick={() => fileInputRef.current?.click()}
                                        loading={uploading}
                                        fullWidth
                                    >
                                        {pdfInfo ? pdfInfo.filename : 'Загрузить PDF'}
                                    </Button>
                                </Box>

                                {/* Page settings */}
                                {pdfInfo && (
                                    <>
                                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                            {pdfInfo.totalPages} страниц в файле
                                        </Typography>
                                        <Stack direction="row" spacing={2}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 500 }}>
                                                    Начать со страницы
                                                </Typography>
                                                <Input
                                                    type="number"
                                                    value={startPage}
                                                    onChange={(e) => setStartPage(Math.max(0, parseInt(e.target.value) || 0))}
                                                    slotProps={{ input: { min: 0, max: pdfInfo.totalPages - 1 } }}
                                                    size="sm"
                                                />
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 500 }}>
                                                    Кол-во страниц
                                                </Typography>
                                                <Input
                                                    type="number"
                                                    value={pageCount}
                                                    onChange={(e) => setPageCount(Math.max(1, parseInt(e.target.value) || 1))}
                                                    slotProps={{ input: { min: 1, max: 30 } }}
                                                    size="sm"
                                                />
                                            </Box>
                                        </Stack>

                                        <Button
                                            startDecorator={<Sparkle />}
                                            onClick={() => handleGenerate('pdf')}
                                            loading={generating}
                                        >
                                            Генерировать из PDF
                                        </Button>
                                    </>
                                )}
                            </Stack>
                        </TabPanel>

                        {/* === Text Tab === */}
                        <TabPanel value="text" sx={{ p: 0 }}>
                            <Stack spacing={2}>
                                <Textarea
                                    placeholder="Вставьте исходный текст из учебника..."
                                    value={sourceText}
                                    onChange={(e) => setSourceText(e.target.value)}
                                    minRows={6}
                                    maxRows={15}
                                    sx={{ fontSize: 'sm' }}
                                />
                                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                    {sourceText.length > 0 ? `${sourceText.length.toLocaleString()} символов` : 'Макс. 35 000 символов'}
                                </Typography>
                                <Button
                                    startDecorator={<Sparkle />}
                                    onClick={() => handleGenerate('text')}
                                    loading={generating}
                                    disabled={!sourceText.trim()}
                                >
                                    Генерировать из текста
                                </Button>
                            </Stack>
                        </TabPanel>
                    </Tabs>

                    {/* Generating progress */}
                    {generating && (
                        <Box sx={{ mt: 2 }}>
                            <LinearProgress sx={{ mb: 1 }} />
                            <Typography level="body-xs" sx={{ color: 'text.tertiary', textAlign: 'center' }}>
                                AI генерирует содержание... обычно 30–60 секунд
                            </Typography>
                        </Box>
                    )}
                </Sheet>
            )}

            {/* ===================== */}
            {/* AI Generated Preview (initial content generation) */}
            {/* ===================== */}
            {isAdmin && genPreview && (
                <Box sx={{ mb: 3 }}>
                    <Alert color="success" variant="soft" sx={{ mb: 2 }}>
                        Содержание сгенерировано. Просмотрите и сохраните.
                    </Alert>

                    <Sheet variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 'xl', mb: 2 }}>
                        <SafeHTML
                            html={genPreview}
                            sx={{
                                '& h3': { mt: 3, mb: 1.5, fontSize: '1.25rem', fontWeight: 700 },
                                '& h4': { mt: 2, mb: 1, fontSize: '1.1rem', fontWeight: 600 },
                                '& p': { mb: 1.5, lineHeight: 1.7 },
                                '& ul, & ol': { mb: 1.5, pl: 3 },
                                '& li': { mb: 0.5, lineHeight: 1.6 },
                                '& b, & strong': { fontWeight: 700 },
                                '& table': { width: '100%', borderCollapse: 'collapse', mb: 2, fontSize: 'sm' },
                                '& thead': { bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(237, 232, 223, 0.10)' : '#EDE8DF' },
                                '& th': { fontWeight: 700, textAlign: 'left', color: (theme) => theme.palette.mode === 'dark' ? 'inherit' : 'inherit' },
                                '& th, & td': { border: '1px solid', borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'neutral.300', p: 1 },
                            }}
                        />
                    </Sheet>

                    <Stack direction="row" spacing={1.5}>
                        <Button
                            variant="solid"
                            startDecorator={<UploadSimple />}
                            onClick={() => handleSaveGenerated(true)}
                            loading={saving}
                        >
                            Сохранить и опубликовать
                        </Button>
                        <Button
                            variant="outlined"
                            color="neutral"
                            startDecorator={<FloppyDisk />}
                            onClick={() => handleSaveGenerated(false)}
                            loading={saving}
                        >
                            Сохранить как черновик
                        </Button>
                        <Button
                            variant="plain"
                            color="neutral"
                            onClick={() => setGenPreview('')}
                        >
                            Отмена
                        </Button>
                        <Box sx={{ flex: 1 }} />
                        <Button
                            variant="plain"
                            color="warning"
                            startDecorator={<Sparkle />}
                            onClick={() => {
                                setGenPreview('');
                                setGenError('');
                            }}
                        >
                            Перегенерировать
                        </Button>
                    </Stack>
                </Box>
            )}

            {/* ===================== */}
            {/* Expand Preview (explanations generation) */}
            {/* ===================== */}
            {isAdmin && expandPreview && (
                <Box sx={{ mb: 3 }}>
                    <Alert color="success" variant="soft" sx={{ mb: 2 }}>
                        Расширенная версия готова. Просмотрите и сохраните.
                    </Alert>

                    <Sheet variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 'xl', mb: 2 }}>
                        <SafeHTML
                            html={expandPreview}
                            sx={{
                                '& h3': { mt: 3, mb: 1.5, fontSize: '1.25rem', fontWeight: 700 },
                                '& h4': { mt: 2, mb: 1, fontSize: '1.1rem', fontWeight: 600 },
                                '& p': { mb: 1.5, lineHeight: 1.7 },
                                '& ul, & ol': { mb: 1.5, pl: 3 },
                                '& li': { mb: 0.5, lineHeight: 1.6 },
                                '& b, & strong': { fontWeight: 700 },
                                '& table': { width: '100%', borderCollapse: 'collapse', mb: 2, fontSize: 'sm' },
                                '& thead': { bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(237, 232, 223, 0.10)' : '#EDE8DF' },
                                '& th': { fontWeight: 700, textAlign: 'left', color: (theme) => theme.palette.mode === 'dark' ? 'inherit' : 'inherit' },
                                '& th, & td': { border: '1px solid', borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'neutral.300', p: 1 },
                            }}
                        />
                    </Sheet>

                    <Stack direction="row" spacing={1.5}>
                        <Button
                            variant="solid"
                            startDecorator={<FloppyDisk />}
                            onClick={handleSaveExpanded}
                            loading={saving}
                        >
                            Сохранить Explanations
                        </Button>
                        <Button
                            variant="plain"
                            color="neutral"
                            onClick={() => setExpandPreview('')}
                        >
                            Отмена
                        </Button>
                        <Box sx={{ flex: 1 }} />
                        <Button
                            variant="plain"
                            color="warning"
                            size="sm"
                            startDecorator={<Sparkle size={16} />}
                            onClick={() => {
                                setExpandPreview('');
                                handleExpandContent();
                            }}
                        >
                            Перегенерировать
                        </Button>
                    </Stack>
                </Box>
            )}

            {/* ===================== */}
            {/* Content (for published/filled modules) */}
            {/* ===================== */}
            {hasContent && !genPreview && !expandPreview && (
                <>
                    {/* Mode toggle: HIGH YIELD / EXPLANATIONS */}
                    {!editing && (
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                            <ButtonGroup
                                variant="outlined"
                                size="sm"
                                sx={{
                                    '--ButtonGroup-radius': '20px',
                                    '--ButtonGroup-separatorSize': '0px',
                                }}
                            >
                                <Button
                                    variant={viewMode === 'high_yield' ? 'solid' : 'outlined'}
                                    color={viewMode === 'high_yield' ? 'neutral' : 'neutral'}
                                    startDecorator={<Lightning size={16} />}
                                    onClick={() => setViewMode('high_yield')}
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '0.8rem',
                                        px: 2,
                                        ...(viewMode === 'high_yield' && {
                                            bgcolor: '#37352F',
                                            color: '#fff',
                                            borderColor: '#37352F',
                                            '&:hover': { bgcolor: '#4a4840' },
                                        }),
                                    }}
                                >
                                    High Yield
                                </Button>
                                <Button
                                    variant={viewMode === 'explanations' ? 'solid' : 'outlined'}
                                    color={viewMode === 'explanations' ? 'neutral' : 'neutral'}
                                    startDecorator={<BookOpen size={16} />}
                                    onClick={() => hasExpanded && setViewMode('explanations')}
                                    disabled={!hasExpanded}
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '0.8rem',
                                        px: 2,
                                        ...(viewMode === 'explanations' && {
                                            bgcolor: '#37352F',
                                            color: '#fff',
                                            borderColor: '#37352F',
                                            '&:hover': { bgcolor: '#4a4840' },
                                        }),
                                    }}
                                >
                                    Explanations
                                </Button>
                            </ButtonGroup>

                            {/* Language toggle EN/RU */}
                            {hasRussian && (
                                <Button
                                    variant={lang === 'ru' ? 'solid' : 'outlined'}
                                    color="neutral"
                                    size="sm"
                                    startDecorator={<Translate size={16} />}
                                    onClick={toggleLang}
                                    sx={{
                                        borderRadius: '20px',
                                        fontSize: '0.78rem',
                                        fontWeight: 600,
                                        px: 2,
                                        ...(lang === 'ru' && {
                                            bgcolor: '#B84C31',
                                            color: '#fff',
                                            borderColor: '#B84C31',
                                            '&:hover': { bgcolor: '#9a3f28' },
                                        }),
                                    }}
                                >
                                    {lang === 'ru' ? 'RU' : 'EN'}
                                </Button>
                            )}

                            {/* Admin: generate explanations button */}
                            {isAdmin && !hasExpanded && (
                                <Button
                                    variant="soft"
                                    color="neutral"
                                    size="sm"
                                    startDecorator={<Brain size={16} />}
                                    onClick={handleExpandContent}
                                    loading={expanding}
                                    sx={{ borderRadius: '20px', fontSize: '0.78rem' }}
                                >
                                    Сгенерировать
                                </Button>
                            )}
                        </Stack>
                    )}

                    {/* Expanding progress */}
                    {expanding && (
                        <Box sx={{ mb: 2 }}>
                            <LinearProgress sx={{ mb: 1 }} />
                            <Typography level="body-xs" sx={{ color: 'text.tertiary', textAlign: 'center' }}>
                                AI добавляет объяснения и механизмы... 1–2 минуты
                            </Typography>
                        </Box>
                    )}

                    {/* Expand error */}
                    {expandError && (
                        <Alert color="danger" variant="soft" sx={{ mb: 2 }} endDecorator={
                            <Button size="sm" variant="soft" color="danger" onClick={() => setExpandError('')}>OK</Button>
                        }>
                            {expandError}
                        </Alert>
                    )}

                    {/* Content sheet */}
                    <Sheet variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 'xl', mb: 3, position: 'relative' }}>
                        {/* Font size controls */}
                        {!editing && (
                            <Stack
                                direction="row"
                                spacing={0.25}
                                sx={{
                                    position: 'absolute',
                                    top: 12,
                                    right: 12,
                                    zIndex: 1,
                                    bgcolor: 'background.surface',
                                    borderRadius: 'lg',
                                    border: '1px solid',
                                    borderColor: 'neutral.200',
                                    p: 0.25,
                                }}
                            >
                                <IconButton
                                    size="sm"
                                    variant="plain"
                                    color="neutral"
                                    onClick={() => changeFontSize(-1)}
                                    disabled={fontSize <= 12}
                                    sx={{ minWidth: 28, minHeight: 28, borderRadius: 'sm' }}
                                    title="Уменьшить шрифт"
                                >
                                    <TextAa size={16} />
                                </IconButton>
                                <Typography
                                    level="body-xs"
                                    sx={{
                                        minWidth: 28,
                                        textAlign: 'center',
                                        lineHeight: '28px',
                                        color: 'text.tertiary',
                                        fontWeight: 500,
                                        userSelect: 'none',
                                    }}
                                >
                                    {fontSize}
                                </Typography>
                                <IconButton
                                    size="sm"
                                    variant="plain"
                                    color="neutral"
                                    onClick={() => changeFontSize(1)}
                                    disabled={fontSize >= 24}
                                    sx={{ minWidth: 28, minHeight: 28, borderRadius: 'sm' }}
                                    title="Увеличить шрифт"
                                >
                                    <TextAa size={16} />
                                </IconButton>
                            </Stack>
                        )}

                        {editing ? (
                            <RichTextEditor
                                content={editContent}
                                onChange={setEditContent}
                                token={token}
                            />
                        ) : (
                            <SafeHTML
                                html={(() => {
                                    const useRu = lang === 'ru' && hasRussian;
                                    if (viewMode === 'explanations' && hasExpanded) {
                                        return useRu && module.content_expanded_ru ? module.content_expanded_ru : module.content_expanded;
                                    }
                                    return useRu ? module.content_ru : module.content;
                                })()}
                                sx={{
                                    fontSize: `${fontSize}px`,
                                    '& h3': { mt: 3, mb: 1.5, fontSize: `${fontSize * 1.35}px`, fontWeight: 700 },
                                    '& h4': { mt: 2, mb: 1, fontSize: `${fontSize * 1.15}px`, fontWeight: 600 },
                                    '& p': { mb: 1.5, lineHeight: 1.7 },
                                    '& ul, & ol': { mb: 1.5, pl: 3 },
                                    '& li': { mb: 0.5, lineHeight: 1.6 },
                                    '& b, & strong': { fontWeight: 700 },
                                    '& table': { width: '100%', borderCollapse: 'collapse', mb: 2, fontSize: `${fontSize * 0.9}px` },
                                    '& thead': { bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(237, 232, 223, 0.10)' : '#EDE8DF' },
                                    '& th': { fontWeight: 700, textAlign: 'left', color: (theme) => theme.palette.mode === 'dark' ? 'inherit' : 'inherit' },
                                    '& th, & td': { border: '1px solid', borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'neutral.300', p: 1 },
                                }}
                            />
                        )}
                    </Sheet>

                    {/* Prev / Next navigation */}
                    {(prevModule || nextModule) && (
                        <Stack direction="row" spacing={1} sx={{ mb: 2, justifyContent: 'space-between' }}>
                            <Button
                                {...(prevModule ? { component: RouterLink, to: `/modules/${prevModule.id}${planId ? `?planId=${planId}` : ''}` } : {})}
                                size="sm"
                                startDecorator={<ArrowLeft size={16} />}
                                disabled={!prevModule}
                                sx={{
                                    maxWidth: '40%', justifyContent: 'flex-start', overflow: 'hidden',
                                    py: 0.8, px: 1.5, borderRadius: 'md',
                                    bgcolor: (theme) => theme.palette.mode === 'dark' ? '#F5F5F7' : '#191919',
                                    color: (theme) => theme.palette.mode === 'dark' ? '#191919' : '#FFFFFF',
                                    '&:hover': {
                                        bgcolor: (theme) => theme.palette.mode === 'dark' ? '#E0E0E2' : '#333333',
                                    },
                                    '&:disabled': {
                                        opacity: 0.3,
                                        bgcolor: (theme) => theme.palette.mode === 'dark' ? '#F5F5F7' : '#191919',
                                        color: (theme) => theme.palette.mode === 'dark' ? '#191919' : '#FFFFFF',
                                    },
                                }}
                            >
                                <Typography level="body-xs" noWrap sx={{ color: 'inherit' }}>{prevModule?.title || ''}</Typography>
                            </Button>
                            <Button
                                {...(nextModule ? { component: RouterLink, to: `/modules/${nextModule.id}${planId ? `?planId=${planId}` : ''}` } : {})}
                                size="sm"
                                endDecorator={<ArrowRight size={16} />}
                                disabled={!nextModule}
                                sx={{
                                    maxWidth: '40%', justifyContent: 'flex-end', overflow: 'hidden',
                                    py: 0.8, px: 1.5, borderRadius: 'md',
                                    bgcolor: (theme) => theme.palette.mode === 'dark' ? '#F5F5F7' : '#191919',
                                    color: (theme) => theme.palette.mode === 'dark' ? '#191919' : '#FFFFFF',
                                    '&:hover': {
                                        bgcolor: (theme) => theme.palette.mode === 'dark' ? '#E0E0E2' : '#333333',
                                    },
                                    '&:disabled': {
                                        opacity: 0.3,
                                        bgcolor: (theme) => theme.palette.mode === 'dark' ? '#F5F5F7' : '#191919',
                                        color: (theme) => theme.palette.mode === 'dark' ? '#191919' : '#FFFFFF',
                                    },
                                }}
                            >
                                <Typography level="body-xs" noWrap sx={{ color: 'inherit' }}>{nextModule?.title || ''}</Typography>
                            </Button>
                        </Stack>
                    )}

                    {/* Admin actions */}
                    {isAdmin && !editing && (
                        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                            {hasExpanded && (
                                <Button
                                    variant="soft"
                                    color="neutral"
                                    size="sm"
                                    startDecorator={<Brain size={16} />}
                                    onClick={handleExpandContent}
                                    loading={expanding}
                                >
                                    Перегенерировать Explanations
                                </Button>
                            )}
                            <Button
                                variant="plain"
                                color="warning"
                                size="sm"
                                startDecorator={<Sparkle size={16} />}
                                onClick={() => {
                                    setEditing(false);
                                    setGenPreview('');
                                    setModule(prev => prev ? { ...prev, content: '' } : prev);
                                }}
                            >
                                Перегенерировать High Yield
                            </Button>
                            <Box sx={{ flex: 1 }} />
                            <Button
                                variant="soft"
                                color="neutral"
                                size="sm"
                                startDecorator={<FolderOpen size={16} />}
                                onClick={() => setMoveModalOpen(true)}
                            >
                                Переместить
                            </Button>
                        </Stack>
                    )}
                </>
            )}

            {/* Empty state for non-admin */}
            {!isAdmin && !hasContent && (
                <Sheet variant="outlined" sx={{ p: 5, borderRadius: 'xl', mb: 3, textAlign: 'center' }}>
                    <Typography level="body-md" sx={{ color: 'text.tertiary' }}>
                        Содержание модуля пока не добавлено.
                    </Typography>
                </Sheet>
            )}

            {/* Recent tests for this module (lets user return to in-progress / review completed) */}
            {!fromPlan && recentTests.length > 0 && (
                <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'xl', mb: 2 }}>
                    <Typography level="title-sm" sx={{ mb: 1.5 }}>Твои тесты по этому модулю</Typography>
                    <Stack spacing={1}>
                        {recentTests.map(t => (
                            <Stack
                                key={t.id}
                                component={RouterLink}
                                to={`/test-session/${t.id}`}
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={1}
                                sx={{
                                    p: 1.25,
                                    borderRadius: 'md',
                                    bgcolor: 'background.level1',
                                    '&:hover': { bgcolor: 'background.level2' },
                                    textDecoration: 'none',
                                    color: 'inherit',
                                }}
                            >
                                <Stack sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography level="body-sm" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {t.title}
                                    </Typography>
                                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                        {t.is_completed
                                            ? `Завершён · ${t.correct_count}/${t.total_questions} правильно`
                                            : `В процессе · ${t.completed_questions}/${t.total_questions} (${t.progress_percentage}%)`}
                                    </Typography>
                                </Stack>
                                <Chip
                                    size="sm"
                                    variant={t.is_completed ? 'soft' : 'solid'}
                                    color={t.is_completed ? 'success' : 'primary'}
                                >
                                    {t.is_completed ? 'Разбор' : 'Продолжить'}
                                </Chip>
                            </Stack>
                        ))}
                    </Stack>
                </Sheet>
            )}

            {/* Practice button (hidden when opened from V2 plan) */}
            {module.question_count > 0 && !fromPlan && (
                <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
                    <Button
                        size="lg"
                        variant="solid"
                        startDecorator={<Exam />}
                        onClick={handlePractice}
                        loading={practicing}
                        sx={{ flex: 1 }}
                    >
                        {recentTests.length > 0 ? 'Новый тест' : 'Практика'} ({module.question_count} вопросов)
                    </Button>
                </Stack>
            )}

            {/* Move Module Modal */}
            {isAdmin && module && (
                <MoveModuleModal
                    open={moveModalOpen}
                    onClose={() => setMoveModalOpen(false)}
                    token={token}
                    moduleId={module.id}
                    currentCategoryPath={module.category_path}
                    onMoved={(updatedModule) => {
                        setModule(updatedModule);
                        setMoveModalOpen(false);
                    }}
                />
            )}
        </Box>
    );
}
